import { ChangeEvent, FC, useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
// i18next
import { useTranslation } from 'react-i18next';
// Types
import BannerTypes from 'types/BannerTypes';
import MediaFormats from 'types/MediaFormats';
// Components
import { Input, Select, Textarea } from 'components/Controls';
import ImageUpload from 'components/ImageUpload';
import VideoUpload from 'components/VideoUpload';
import MultiLanguageTextField from 'components/MultiLanguageTextField';
// Utilities
import { isRequired } from 'utilities/validation';

type Props = {
  bannerRef: string;
}

const BannerForm: FC<Props> = ({ bannerRef }) => {
  const { t } = useTranslation();

  const { control, watch, setValue, formState: { errors } } = useFormContext();

  const watchedBanners = watch('banners');
  const bannerIndex = watchedBanners.findIndex((banner: any) => banner.ref === bannerRef);
  const name = `banners.${bannerIndex}`;
  const watchedType = watch(`${name}.type`);

  const getBannerError = useCallback((field: string) => {
    if (!errors || !errors.banners) return '';
    const banner = (errors as any).banners[bannerIndex];
    return banner && banner[field] ? banner[field].message : '';
    // eslint-disable-next-line
  }, [errors]);

  return (
    <div className="w-full landscape:w-1/2 tablet:w-full laptop:w-7/12 px-6">
      <div className="">
        <MultiLanguageTextField
          name={`${name}.title`}
          label={t('labels.title')}
          className="mb-4"
        />
        {watchedType !== BannerTypes.Video ? (
          <Controller
            control={control} name={`${name}.text`}
            rules={{ required: watchedType === BannerTypes.Text ? isRequired : false }}
            render={({ field }) => (
              <Textarea
                {...field}
                label={t('labels.text')}
                id={`${name}.text`}
                name={`${name}.text`}
                rows={6}
                required={watchedType === BannerTypes.Text}
                error={Boolean(getBannerError('text'))}
                helperText={getBannerError('text')}
              />
            )}
          />
        ) : null}
        {watchedType === BannerTypes.Image ? (
          <span className="block text-gray-400 text-sm font-light leading-5 -mt-2 mb-4">{t('pages.quizForm.bannerImageTextHint')}</span>
        ) : null}
        <Controller
          control={control} name={`${name}.type`}
          render={({ field: { value, onChange } }) => (
            <Select
              label={t('labels.type')} id={`${name}.type`} name={`${name}.type`}
              value={value}
              options={typeOptions}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                setValue(`${name}.source`, '');
                onChange(e);
              }}
            />
          )}
        />
        {watchedType !== BannerTypes.Video ? (
          <Controller
            control={control} name={`${name}.timeout`}
            render={({ field }) => (
              <Input
                {...field}
                id={`${name}.timeout`}
                type="number"
                label={t('pages.quizForm.bannerTimeoutLabel')}
                helperText={t('pages.quizForm.bannerTimeoutHint')}
              />
            )}
          />
        ) : null}
        {watchedType !== BannerTypes.Text ? (
          <div className="w-full">
            <Controller
              control={control} name={`${name}.source`}
              rules={{ required: isRequired }}
              render={({ field }) => watchedType === BannerTypes.Video ? (
                <div className="sm:w-1/2">
                  <VideoUpload
                    {...field}
                    required={true}
                    error={Boolean(getBannerError('source'))}
                    errorText={getBannerError('source')}
                  />
                </div>
              ) : (
                <ImageUpload
                  {...field}
                  format={MediaFormats.Interstitial}
                  required={true}
                  error={Boolean(getBannerError('source'))}
                  errorText={getBannerError('source')}
                  className="sm:w-1/2"
                />
              )}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default BannerForm;

const typeOptions = (Object.keys(BannerTypes) as Array<keyof typeof BannerTypes>).map(key => ({
  value: BannerTypes[key],
  label: key,
}));
